import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { type FieldValues, useFormContext } from 'react-hook-form';
import * as yup from 'yup';

type WarningContextType = {
  warnings: Record<string, string>;
};

const WarningContext = createContext<WarningContextType | null>(null);

export const useWarnings = () => useContext(WarningContext);

type WarningProviderProps<T extends yup.AnyObject> = {
  children: ReactNode;
  warningSchema?: yup.ObjectSchema<T>;
};

export const WarningProvider = <T extends yup.AnyObject>({ children, warningSchema }: WarningProviderProps<T>) => {
  const { watch, getValues } = useFormContext<FieldValues>();
  const [warnings, setWarnings] = useState<Record<string, string>>({});

  const validateWarnings = useCallback(
    async (values: FieldValues) => {
      try {
        if (warningSchema) {
          await warningSchema.validate(values, { abortEarly: false });
          setWarnings({});
        }
      } catch (err) {
        if (err instanceof yup.ValidationError && err.inner) {
          const warningMessages = err.inner.reduce((acc: Record<string, string>, currentError) => {
            acc[currentError.path!] = currentError.message;

            return acc;
          }, {});
          setWarnings(warningMessages);
        }
      }
    },
    [warningSchema]
  );

  useEffect(() => {
    const subscription = watch(validateWarnings);

    validateWarnings(getValues());

    return () => subscription.unsubscribe();
  }, [getValues, validateWarnings, watch]);

  const contextValue = useMemo(() => ({ warnings, validateWarnings }), [warnings, validateWarnings]);

  return <WarningContext.Provider value={contextValue}>{children}</WarningContext.Provider>;
};
